import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Injector,
  ViewContainerRef,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize, Observable, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { InsightMode } from '../../../../insights.enums';
import {
  ApiResponses,
  CaseBehaviour,
  CaseDetailModel,
} from '../../../../insights.interface';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzRadioGroupComponent, NzRadioModule } from 'ng-zorro-antd/radio';
import { ReportManagerService } from '../report-manager.service';
import { ApiResponse } from '../../../../../../core/models/api-response.models';
import {
  ProgressReportGraphMapping,
  reportDrawerParams,
  ReportPrintData,
  ReportRes,
} from '../report-manager.interface';
import { CommonService } from '../../../../../../shared/services/common.service';
import { ConfirmDialogComponent } from '../../../../../../components/confirm-dialog/confirm-dialog.component';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { FormsModule } from '@angular/forms';
import { CreateReportComponent } from '../create-report/create-report.component';
import {
  BehaviorGraphDataResponse,
  frequencyDatasheetGraph,
  frequencyIntervalGraph,
  GraphList,
  latencyGraph,
  caseBehaviorUnitMappings,
  partialIntervalGraph,
  replacementDataType,
  CaseBehaviorUnitMapping,
} from '../../../insights-data/insights-data.interface';
import { InsightsDataService } from '../../../insights-data/insights-data.service';
import _ from 'lodash';
import { ManageGraphComponent } from '../../../insights-data/manage-graph.component';
import { PrintReportComponent } from '../print-report/print-report.component';
import { PrintBipService } from '../../print-options/print-bip.service';
import { OrganizationModel } from '../../../../../organizations/organization.interface';
import { OrganizationService } from '../../../../../organizations/organization.service';
import { DrawerHeaderComponent } from '../../../../../../components/drawer-header/drawer-header.component';

@Component({
  selector: 'app-edit-report',
  standalone: true,
  imports: [
    SvgIconComponent,
    CommonModule,
    NzSpinModule,
    NzButtonModule,
    NzTableModule,
    NzRadioModule,
    NzRadioGroupComponent,
    ConfirmDialogComponent,
    FormsModule,
    DrawerHeaderComponent,
  ],
  templateUrl: './edit-report.component.html',
  styleUrl: './edit-report.component.scss',
})
export class EditReportComponent
  extends ManageGraphComponent
  implements OnDestroy
{
  loading$: Observable<boolean>;
  @ViewChild('editTpl') editTpl!: TemplateRef<void>;
  private readonly destroy$ = new Subject<void>();
  caseParams: reportDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.PRINT_CASE,
  };
  stepNumber = 1;
  caseDetail: CaseDetailModel = {} as CaseDetailModel;
  isDeleteVisible = false;
  reportList: ReportRes[] = [];
  isDeleting = false;
  deleteId = 0;
  selectedReport: ReportRes | null = null;
  printRecord: ReportRes | null = null;
  graphList: GraphList[] = [];
  checkedGraphList: GraphList[] = [];
  datasheetData: { id: number; value: string }[] = [
    { id: 1, value: 'Frequency Based Datasheet' },
    { id: 2, value: 'Latency' },
    { id: 3, value: 'Partial Interval' },
    { id: 4, value: 'Replacement Behavior' },
  ];

  organizationDetail?: OrganizationModel;
  printData: ReportPrintData[] = [];
  constructor(
    private loaderService: LoaderService,
    private drawerService: DrawerService,
    private reportService: ReportManagerService,
    private commonService: CommonService,
    private dataService: InsightsDataService,
    private viewContainerRef: ViewContainerRef,
    private printBipService: PrintBipService,
    private injector: Injector,
    private orgService: OrganizationService,
    cd: ChangeDetectorRef
  ) {
    super(cd);
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
    this.getReportList();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<reportDrawerParams>(DRAWER_IDS.EDIT_REPORT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.caseDetail = params.case;
          if (this.caseDetail.student.organizationId) {
            this.getOrgById(this.caseDetail.student.organizationId);
          }
        }
      });
  }
  getReportList() {
    this.loaderService.show();
    this.reportService
      .getReports(this.caseParams.case.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponse<ReportRes[]>) => {
          if (res.result) {
            this.reportList = res.result;
          }
          this.loaderService.hide();
        },
        error: (error: Error) => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  private getOrgById(organizationId: number): void {
    if (!organizationId) return;
    this.loaderService.show();
    this.orgService
      .getOrganization(organizationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: res => {
          this.organizationDetail = res;
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  printReport(data: ReportRes) {
    this.printRecord = data;
    if (this.printRecord) {
      this.loaderService.show();
      this.getGraphs();
    }
  }

  getGraphs() {
    this.dataService
      .getGraphList(this.caseDetail.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponses<GraphList>) => {
          if (res.result) {
            this.graphList = res.result;
            if (
              this.printRecord &&
              this.printRecord.progressReportGraphMappings.length > 0
            ) {
              this.processMappedArray();
            } else {
              this.loaderService.hide();
            }
            this.cd.markForCheck();
          } else {
            this.loaderService.hide();
          }
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
          this.loaderService.hide();
        },
      });
  }
  async processMappedArray() {
    const mappedArray = _.chain(this.graphList)
      .filter(graph =>
        _.some(this.printRecord?.progressReportGraphMappings, {
          activityDataUnitId: graph.id,
        })
      )
      .map(graph => ({
        ...graph,
        comment: _.get(
          _.find(this.printRecord?.progressReportGraphMappings, {
            activityDataUnitId: graph.id,
          }),
          'comment',
          ''
        ),
      }))
      .value();

    this.checkedGraphList = mappedArray;

    if (mappedArray.length === 0) {
      this.loaderService.hide();
      return;
    }
    this.getAllBehaviorData(mappedArray);
  }

  async getAllBehaviorData(mappedArray: GraphList[]) {
    const caseBehaviorUnitMappings: caseBehaviorUnitMappings[] = [];
    const startDate = this.printRecord?.startDate
      ? new Date(this.printRecord.startDate)
      : null;
    const endDate = this.printRecord?.endDate
      ? new Date(this.printRecord.endDate)
      : null;

    for (const graph of mappedArray) {
      try {
        const showIntervalFrequency =
          this.printRecord?.showIntervalFrequency || false;
        let selectedUnit = this.datasheetData.find(
          d => d.value === graph.unitName
        );

        if (!selectedUnit && graph.unitName.includes('Frequency')) {
          selectedUnit = this.datasheetData.find(d => d.id === 1);
        }

        if (selectedUnit) {
          caseBehaviorUnitMappings.push({
            caseBehaviorId: graph.caseBehaviorId,
            unitName: selectedUnit.id,
            graphType: selectedUnit.id === 1 ? 1 : 0,
          });

          if (selectedUnit.id === 1 && showIntervalFrequency) {
            caseBehaviorUnitMappings.push({
              caseBehaviorId: graph.caseBehaviorId,
              unitName: selectedUnit.id,
              graphType: 2,
            });
          }
        }
      } catch (error) {
        console.error('Error preparing graph payload:', graph.id, error);
      }
    }

    if (caseBehaviorUnitMappings.length > 0) {
      this.loaderService.show();
      try {
        const res = await this.dataService
          .getMultipleGraphData({
            caseBehaviorUnitMappings,
            startDate: startDate ? startDate.toISOString().split('T')[0] : '',
            endDate: endDate ? endDate.toISOString().split('T')[0] : '',
            viewType: this.printRecord?.viewType ?? 1,
          })
          .pipe(take(1), takeUntil(this.destroy$))
          .toPromise();

        if (
          res &&
          res.result &&
          res.result.caseBehaviorUnitMappings.length > 0
        ) {
          const returned = res.result
            .caseBehaviorUnitMappings as unknown as CaseBehaviorUnitMapping[];
          for (const element of returned) {
            const unitName = element.unitName;
            const selectedUnit =
              this.datasheetData.find(d => d.value === unitName) ||
              this.datasheetData.find(d => d.value === element.unitName);
            const graphType =
              element.graphType ?? (selectedUnit?.id === 1 ? 1 : 0);

            const mappedData: BehaviorGraphDataResponse = {
              caseBehaviorId: element.caseBehaviorId,
              unitName: unitName,
              partialInterval:
                selectedUnit?.id === 3
                  ? (element.graph as partialIntervalGraph)
                  : null,
              frequencyDuration:
                selectedUnit?.id === 1 && graphType === 1
                  ? (element.graph as frequencyDatasheetGraph)
                  : null,
              frequencyInterval:
                selectedUnit?.id === 1 && graphType === 2
                  ? (element.graph as frequencyIntervalGraph)
                  : null,
              latency:
                selectedUnit?.id === 2 ? (element.graph as latencyGraph) : null,
              replacementBehavior:
                selectedUnit?.id === 4
                  ? (element.graph as replacementDataType)
                  : null,
            };

            const originalGraph = this.checkedGraphList.find(
              g => g.caseBehaviorId === mappedData.caseBehaviorId
            );

            await this.manageBehvaiorGraphResponseData(
              mappedData,
              selectedUnit?.id || 0,
              mappedData.caseBehaviorId,
              originalGraph || ({} as GraphList),
              graphType
            );
          }

          await this.print();
        }
      } catch (error) {
        console.error('Error fetching multiple graph data:', error);
        this.commonService.notify(
          'error',
          (error as Error).message || 'Error fetching graphs'
        );
      } finally {
        this.loaderService.hide();
      }
    } else {
      this.loaderService.hide();
      await this.print();
    }
  }

  async manageBehvaiorGraphResponseData(
    data: BehaviorGraphDataResponse,
    selectedUnit: number,
    selectedGraph: number,
    graphData: GraphList,
    graphType: number
  ) {
    try {
      const success = await this.processBehaviorGraphData(
        data,
        selectedUnit,
        graphType,
        this.printRecord?.viewType || 1,
        this.printRecord?.isTrendLineEnabled || false,
        async (unit, graphDataResult, showInterval) => {
          this.checkedGraphList = await this.generateImage(
            unit,
            selectedGraph,
            graphDataResult,
            showInterval,
            data
          );
        }
      );

      if (!success) {
        graphData.graphImage = [];
        graphData.graphImageTotal = [];
        graphData.graphData = undefined;
      }
    } catch (error) {
      console.error('Error in manageGraphResponseData:', error);
      throw error;
    }
  }

  async generateImage(
    selectedUnit: number,
    selectedGraph: number,
    data:
      | frequencyDatasheetGraph
      | latencyGraph
      | partialIntervalGraph
      | replacementDataType
      | frequencyIntervalGraph,
    showIntervalFrequency = false,
    behaviorData: BehaviorGraphDataResponse
  ) {
    const unitName =
      this.datasheetData.find(d => d.id === selectedUnit)?.value || '';
    return await this.reportService.generateGraphImage(
      this.viewContainerRef,
      selectedUnit,
      selectedGraph,
      this.checkedGraphList,
      data,
      showIntervalFrequency,
      unitName,
      data.caseBehaviorId || 0,
      behaviorData
    );
  }
  generatePrintData(
    graphList: GraphList[],
    progressReportGraphMappings: ProgressReportGraphMapping[],
    caseBehaviors: CaseBehaviour[]
  ): boolean {
    const result = progressReportGraphMappings.map(mapping => {
      const matchingGraph = graphList.find(
        graph => graph.id === mapping.activityDataUnitId
      );
      const matchingBehavior = caseBehaviors.find(
        behavior => behavior.id === matchingGraph?.caseBehaviorId
      );

      return {
        category: matchingGraph?.behaviorCategoryName || 'Unknown',
        behavior:
          matchingGraph?.behaviorTypes.map(bt => bt.name).join(', ') ||
          'Unknown',
        operationalDefinition:
          matchingBehavior?.operationalDefinition || 'No Data',
        goals: matchingBehavior?.goals || 'No Goals',
        graphImage: matchingGraph?.graphImage || [],
        graphTotalImage: matchingGraph?.graphImageTotal || [],
      };
    });
    this.printData = result;
    return true;
  }
  async print(): Promise<void> {
    const isGraphDataAvailable = this.generatePrintData(
      this.checkedGraphList,
      this.printRecord?.progressReportGraphMappings ?? [],
      this.caseDetail.caseBehaviors
    );

    if (isGraphDataAvailable) {
      const printReportData = {
        reportData: this.printRecord,
        printData: this.printData,
      };

      const factory =
        this.viewContainerRef.createComponent(PrintReportComponent);

      if (this.caseDetail) {
        const studentAge = this.caseDetail?.student?.dateOfBirth
          ? this.printBipService.calculateAge(
              this.caseDetail?.student?.dateOfBirth
            )
          : null;
        this.caseDetail.age = studentAge ?? 0;
      }

      const componentElement = factory.location.nativeElement;
      componentElement.style.display = 'none';

      this.loaderService.show();

      factory.instance.reportData = {
        organizationDetail: this.organizationDetail,
        caseData: this.caseDetail,
        printReportData: printReportData,
      };

      this.waitForInitialization(factory).then(async () => {
        await factory.instance.printPDF();
        this.loaderService.hide();
        this.cd.detectChanges();
        factory.destroy();
      });
    }

    this.loaderService.hide();
  }

  waitForInitialization(factory: ComponentRef<PrintReportComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          this.loaderService.hide();
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }
  showDeleteReportModal(id: number): void {
    this.isDeleteVisible = true;
    this.deleteId = id;
  }
  handleDelete(): void {
    this.isDeleting = true;
    this.reportService
      .deleteReport(this.deleteId)
      .pipe(
        finalize(() => {
          this.isDeleting = false;
          this.isDeleteVisible = false;
          this.cd.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', 'Report deleted successfully');
          this.getReportList();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  handleDeleteCancel(): void {
    this.isDeleteVisible = false;
  }

  onNext() {
    if (this.selectedReport) {
      this.drawerService.openDrawer(
        CreateReportComponent,
        this.editTpl as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseDetail,
          mode: InsightMode.EDIT_REPORT,
          selectedReport: this.selectedReport,
        },
        DRAWER_IDS.CREATE_REPORT
      );
    }
  }
  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_REPORT);
  }
  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.EDIT_REPORT);
  }
}
